import { ApiSet, BaseResponse, IndexApiSet, useDeleteApi, useIndexApi, usePostApi, usePutApi } from 'utils/network/api_hooks'
import { Form, useEffectSkipFirst } from 'utils/hooks'
import { CraifHttpClient } from '../utils/network/axios'
import { PagingResponse } from 'entities'
import { ProductItem, ProductItemForm, ProductItemSearchForm } from 'entities/product_item'

type ProductItemsResponse = PagingResponse & {
    results: ProductItem[]
}

export function useFetchProductItemsApi(searchForm?: Form<ProductItemSearchForm>): IndexApiSet<ProductItemsResponse> & { execute: () => void } {
    const apiPath = 'product_items/'
    const api = useIndexApi<ProductItemsResponse>(new CraifHttpClient(), {
        initialResponse: { count: 0, results: [] },
        initialState: { page: searchForm?.object.page || 1, perPage: searchForm?.object.perPage || 50 },
    })

    const execute = (): void => {
        api.execute(apiPath, { params: searchForm?.object })
    }

    useEffectSkipFirst(() => {
        searchForm?.update((f) => {
            if (api.pageSet.page) f.page = api.pageSet.page
            if (api.pageSet.perPage) f.perPage = api.pageSet.perPage
        })
    }, [api.pageSet.page, api.pageSet.perPage])

    useEffectSkipFirst(() => {
        execute()
    }, [searchForm?.object])

    return { ...api, execute: execute }
}

export function usePostProductItemApi(): ApiSet<BaseResponse> & { execute: (form: Form<ProductItemForm>) => void } {
    const apiSet = usePostApi<BaseResponse, ProductItemForm>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<ProductItemForm>) => {
        const apiPath = `product_items/`
        apiSet.execute(apiPath, form)
    }

    return { ...apiSet, execute: execute }
}

export function usePutProductItemApi(): ApiSet<BaseResponse> & { execute: (form: Form<ProductItemForm>) => void } {
    const apiSet = usePutApi<BaseResponse, ProductItemForm>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<ProductItemForm>) => {
        const apiPath = `product_items/${form.object.id}/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useBulkPutProductItemsApi(): ApiSet<BaseResponse> & { execute: (form: Form<ProductItemForm[]>) => void } {
    const apiSet = usePutApi<BaseResponse, ProductItemForm[]>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<ProductItemForm[]>) => {
        const apiPath = `product_items/bulk_update/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useDeleteProductItemApi(): ApiSet<BaseResponse> & {
    execute: (id: number) => void
} {
    const api = useDeleteApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number): void => {
        const apiPath = `product_items/${id}/`
        api.execute(apiPath)
    }

    return { ...api, execute: execute }
}
